import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Zap, Users, Monitor, RefreshCw, Download } from 'lucide-react'
import {
  getActivityLog,
  getRealtimeSnapshot,
  type AuditLogEvent,
  type RealtimeSnapshot,
} from '../../../lib/api'
import { useAutoRefresh } from '../../../hooks/useAutoRefresh'
import StatCard from '../../../components/analytics/StatCard'
import { arrayToCSV, downloadCSV } from '../../../lib/csv-export'
import ActivityTimeline from '../../../components/ActivityTimeline'
import { AnalyticsNav } from './AnalyticsOverview'

/**
 * /admin/analytics/activity — Live activity tab.
 *
 * Two distinct widgets:
 *   1. "Right Now" snapshot — running apps / active workspaces /
 *      connected users. Pulled from /admin/realtime; not duplicated
 *      anywhere else on the dashboard, this is the unique value-add of
 *      this tab over /admin/activity.
 *   2. Recent Events list — same unified audit_log ∪ platform_events
 *      feed used by /admin/activity. Used to be a parallel rendering
 *      built on a different ActivityEvent shape; now reuses
 *      <ActivityTimeline> so labels, slot/anon badges, detail hints,
 *      and drilldown links all match the rest of the admin surfaces.
 *
 * Auto-refreshes every 30s like other analytics widgets.
 */
export default function ActivityFeed() {
  const navigate = useNavigate()
  const [events, setEvents] = useState<AuditLogEvent[]>([])
  const [realtime, setRealtime] = useState<RealtimeSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [feed, realtimeData] = await Promise.all([
        getActivityLog({ limit: 50 }),
        getRealtimeSnapshot(),
      ])
      setEvents(feed.events)
      setRealtime(realtimeData)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity feed')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useAutoRefresh(fetchData, { baseInterval: 30000, maxInterval: 300000 })

  function handleExportCSV() {
    if (events.length === 0) return
    const rows = events.map((e) => ({
      created_at: e.created_at,
      source: e.source,
      action: e.action,
      slot: e.slot ?? '',
      user_id: e.user_id ?? '',
      email: e.email ?? '',
      resource_type: e.resource_type ?? '',
      resource_id: e.resource_id ?? '',
      ip_address: e.ip_address ?? '',
    }))
    downloadCSV(
      arrayToCSV(rows),
      `activity-feed-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.csv`,
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Live Activity</h1>
          <p className="text-muted-foreground">Real-time platform stats and recent events</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            disabled={events.length === 0}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-secondary transition-colors text-sm disabled:opacity-50"
            title="Download data as CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <AnalyticsNav />

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {loading && !realtime ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              <h2 className="text-lg font-semibold">Right Now</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard label="Running Apps" value={realtime?.running_apps ?? 0} icon={Zap} />
              <StatCard
                label="Active Workspaces"
                value={realtime?.active_workspaces ?? 0}
                icon={Monitor}
              />
              <StatCard
                label="Connected Users"
                value={realtime?.connected_users ?? 0}
                icon={Users}
              />
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">Recent Events</h2>
            <ActivityTimeline
              events={events}
              loading={loading}
              showUser
              onEventClick={(event) => {
                if (event.user_id) navigate(`/admin/users/${event.user_id}`)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
